// Открыта ли экранная клавиатура — для липких футеров (кнопка печати, футер табло).
//
// Замечание с точки 2026-10-02: на телефоне с открытой клавиатурой кнопка «Печать
// этикетки» висела посреди экрана, а не на клавиатуре. Футеры отступают снизу на
// `--kiosk-nav-height` = высота системной панели навигации Android (`--ds-inset-bottom`,
// её выставляет Capacitor, B-055). Клавиатура эту панель закрывает, WebView ужимается до
// верха клавиатуры, а отступ остаётся — ровно на высоту панели (32dp у жестов, 48dp
// у трёх кнопок). Пока клавиатура открыта, отступ должен быть нулевым.
//
// Клавиатура делает это ДВУМЯ разными способами, и признак нужен на оба (замечание
// владельца 2026-10-03 про футер табло):
//
//  1. Сжимается сам слой страницы (`window.innerHeight`) — так ведёт себя оболочка
//     Capacitor (`adjustResize`). Липкий футер остаётся у нижнего края видимого и
//     никуда не едет; лишним остаётся только отступ под панель жеста.
//  2. Слой страницы НЕ меняется, ужимается только видимая область
//     (`visualViewport.height`) — так ведёт себя Chrome на Android. Замерено на
//     Xiaomi (Android 16): `innerHeight` 721, видимая область 417. Футер при этом
//     стоит на 300px НИЖЕ видимого края, за клавиатурой, и при свайпе выезжает
//     из-под неё и обратно — это и есть «кнопка ездит».
//
// Второй случай одной лишь отменой отступа не лечится: футер надо поднять на высоту
// перекрытия. Её несёт `--ds-keyboard-overlap`, и в первом случае она честно равна
// нулю — поведение оболочки не меняется.
//
// Признак — РАЗМЕР, а не фокус в поле: клавиатуру прячут кнопкой «назад», фокус при
// этом остаётся, и футер уехал бы под полосу жеста. Ширина — ключ замера: поворот
// меняет высоту законно, и сравнивать с высотой другой ориентации нельзя.
//
// Результат — `data-keyboard="open"` и `--ds-keyboard-overlap` на `<html>`,
// правила — в `reset.css` ui-kit.

/** Меньшее сокращение — не клавиатура: панель адреса браузера, смена системных панелей. */
export const KEYBOARD_MIN_HEIGHT = 120;

export function watchSoftKeyboard(win: Window = window): () => void {
  const root = win.document.documentElement;
  let width = win.innerWidth;
  let fullHeight = win.innerHeight;

  const update = (): void => {
    if (win.innerWidth !== width) {
      width = win.innerWidth;
      fullHeight = win.innerHeight;
    }
    fullHeight = Math.max(fullHeight, win.innerHeight);

    // Сжатие слоя страницы (оболочка) и перекрытие видимой области (браузер) — два
    // независимых замера одного события. Берём больший: ненулевым будет ровно тот,
    // которым клавиатура проявила себя в этой среде.
    const shrunk = fullHeight - win.innerHeight;
    const visual = win.visualViewport;
    // `offsetTop` — насколько видимая область уже сдвинута вниз по слою страницы;
    // без него перекрытие занижается ровно на эту величину при прокрутке.
    const overlap = visual
      ? Math.max(0, Math.round(win.innerHeight - visual.height - visual.offsetTop))
      : 0;

    if (Math.max(shrunk, overlap) >= KEYBOARD_MIN_HEIGHT) {
      root.dataset.keyboard = 'open';
      // Ноль в среде, где слой страницы сжимается сам: футер и так у видимого края.
      root.style.setProperty('--ds-keyboard-overlap', `${String(overlap)}px`);
    } else {
      delete root.dataset.keyboard;
      root.style.removeProperty('--ds-keyboard-overlap');
    }
  };

  win.addEventListener('resize', update);
  // Видимая область меняется и БЕЗ `resize` окна: открытие клавиатуры в браузере,
  // прокрутка под ней. Без этих двух подписок второй случай не виден вовсе.
  win.visualViewport?.addEventListener('resize', update);
  win.visualViewport?.addEventListener('scroll', update);
  update();
  return () => {
    win.removeEventListener('resize', update);
    win.visualViewport?.removeEventListener('resize', update);
    win.visualViewport?.removeEventListener('scroll', update);
    delete root.dataset.keyboard;
    root.style.removeProperty('--ds-keyboard-overlap');
  };
}
